import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { ChevronRight } from 'lucide-react';

const segmentLabels = {
  nl: {
    'werkwijze': 'Werkwijze',
    'diensten': 'Diensten',
    'seo-pakketten': 'SEO Pakketten',
    'seo-consultant': 'SEO Consultant',
    'over-mij': 'Over mij',
    'contact': 'Contact',
    'intake': 'Intake',
    'blog': 'Blog',
    'disclaimer': 'Disclaimer',
    'privacyverklaring': 'Privacyverklaring',
    'freelance-seo-consultant': 'Freelance SEO Consultant',
    // Services
    'seo-strategie': 'SEO Strategie',
    'technische-seo': 'Technische SEO',
    'seo-tekstschrijver': 'SEO Teksten',
    'linkbuilding': 'Linkbuilding',
    'local-seo': 'Lokale SEO',
    'ecommerce-seo': 'Ecommerce SEO',
    'migratie': 'Migratie',
    'seo-consultancy': 'SEO Consultancy',
    'geo': 'Internationale SEO',
    'traffic-recovery-audit': 'Traffic Recovery Audit',
    'content-performance-audit': 'Content Performance Audit',
    'full-scope-audit': 'Full Scope Audit',
    // Cities
    'veenendaal': 'Veenendaal',
    'utrecht': 'Utrecht',
    'wageningen': 'Wageningen',
    'ede': 'Ede',
    'hengelo': 'Hengelo',
    'emmen': 'Emmen',
    'asten': 'Asten',
    'amsterdam': 'Amsterdam',
    // Sectors
    'restaurant': 'Restaurant',
    'mkb': 'MKB',
    'loodgieter': 'Loodgieter',
    'webshop': 'Webshop',
    'advocaat': 'Advocaat',
    'makelaar': 'Makelaar',
    'zorg': 'Zorg',
    'horeca': 'Horeca',
    'fysiotherapeut': 'Fysiotherapeut',
    'gehandicaptenzorg': 'Gehandicaptenzorg',
    'orthodontist': 'Orthodontist',
    'dermatoloog': 'Dermatoloog',
    'vastgoed': 'Vastgoed',
    'jurist': 'Jurist',
    'implantologie': 'Implantologie',
    'accountant': 'Accountant',
    'huisarts': 'Huisarts',
    'logopedie': 'Logopedie',
  },
  en: {
    'how-i-work': 'How I Work',
    'services': 'Services',
    'seo-packages': 'SEO Packages',
    'seo-consultant': 'SEO Consultant',
    'about': 'About',
    'contact': 'Contact',
    'intake': 'Intake',
    'blog': 'Blog',
    'disclaimer': 'Disclaimer',
    'privacy-policy': 'Privacy Policy',
    // Services (English slugs)
    'seo-strategy': 'SEO Strategy',
    'technical-seo': 'Technical SEO',
    'seo-copywriting': 'SEO Copywriting',
    'link-building': 'Link Building',
    'local-seo': 'Local SEO',
    'ecommerce-seo': 'Ecommerce SEO',
    'migration': 'Migration',
    'seo-consultancy': 'SEO Consultancy',
    'geo': 'International SEO',
    'traffic-recovery-audit': 'Traffic Recovery Audit',
    'content-performance-audit': 'Content Performance Audit',
    'full-scope-audit': 'Full Scope Audit',
  }
};

export default function Breadcrumbs() {
  const location = useLocation();
  const { language } = useLanguage();
  const pathname = location.pathname;

  // Don't show on homepage
  if (pathname === '/' || pathname === '/en' || pathname === '/en/') return null;

  const isEnglish = pathname.startsWith('/en');
  const cleanPath = isEnglish ? pathname.replace(/^\/en\/?/, '') : pathname.replace(/^\//, '');
  const segments = cleanPath.split('/').filter(Boolean);

  if (segments.length === 0) return null;

  const labels = segmentLabels[language] || segmentLabels.nl;
  const homeHref = isEnglish ? '/en' : '/';

  const crumbs = segments.map((segment, index) => {
    const pathParts = segments.slice(0, index + 1);
    const href = isEnglish ? `/en/${pathParts.join('/')}` : `/${pathParts.join('/')}`;
    const label = labels[segment] || segment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const isLast = index === segments.length - 1;
    return { href, label, isLast };
  });

  return (
    <nav aria-label="Breadcrumb" className="bg-[#FAFAFA] border-b border-gray-100" data-testid="breadcrumbs">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3">
        <ol className="flex items-center flex-wrap gap-1 text-sm" itemScope itemType="https://schema.org/BreadcrumbList">
          <li className="flex items-center" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
            <Link to={homeHref} className="text-[#999] hover:text-[#4286F4] transition-colors" itemProp="item">
              <span itemProp="name">Home</span>
            </Link>
            <meta itemProp="position" content="1" />
          </li>
          {crumbs.map((crumb, i) => (
            <li key={crumb.href} className="flex items-center" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <ChevronRight className="h-3.5 w-3.5 text-[#CCC] mx-1 flex-shrink-0" />
              {crumb.isLast ? (
                <span className="text-[#08080A] font-medium" itemProp="name">{crumb.label}</span>
              ) : (
                <Link to={crumb.href} className="text-[#999] hover:text-[#4286F4] transition-colors" itemProp="item">
                  <span itemProp="name">{crumb.label}</span>
                </Link>
              )}
              <meta itemProp="position" content={String(i + 2)} />
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}
